import React, { useCallback, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { observer } from "mobx-react";
import { getSnapshot } from "mobx-state-tree";
import { Col, Row } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import MediaQuery, { useMediaQuery } from "react-responsive";

import { useAppStore } from "../providers/AppStoreProvider";
import { useAPI } from "../providers/ApiProvider";
import { VIDEOJS_OPTIONS } from "../config/VideoJS";
import { TOASTIFY_OPTIONS } from "../config/ReactToastify";

import { Tabs } from "./Tabs";
import { Playlist } from "./Playlist/Playlist";
import { VideoJS } from "./Content/VideoPlayer/VideoPlayer";
import {
  AddToCartModal,
  ChapterModal,
  CommentModal,
  LessonModal,
  ProductDetailModal,
  QuestionModal,
  RatingModal,
  ReactionModal,
  ReportModal,
} from "../components/Modal";

import "./App.scss";

const App = observer(() => {
  const store = useAppStore();
  const api = useAPI();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery({ query: "(max-width: 992px)" });

  const { lessonId } = useParams();
  const playerRef = useRef(null);

  useEffect(() => {
    if (lessonId) {
      const lesson = store.lessons.find((lesson) => lesson.id === parseInt(lessonId));

      if (!lesson) {
        toast.error("Không tìm thấy bài học", TOASTIFY_OPTIONS);
      } else if (
        !store.hasPermission("update_lesson") &&
        lesson.status === "private" &&
        !store.hasPermission("view_private_lesson")
      ) {
        toast.warn("Vui lòng đăng ký khoá học để xem được bài học này", TOASTIFY_OPTIONS);
      } else {
        if (
          store.hasPermission("view_private_lesson") &&
          lesson.lesson_type === "video" &&
          lesson.video?.status !== "available"
        ) {
          toast.warn("Video đang được xử lý, vui lòng thử lại sau ít phút", TOASTIFY_OPTIONS);
        }

        store.setCurrentLesson(parseInt(lesson.id));
        if (store.currentLesson.progress < 0) {
          viewLessonHandler();
        }

        if (lesson.lesson_type === "video") {
          // Wait for assigning ref
          setTimeout(() => {
            if (lesson.progress >= 95) {
              setCurrentTime(0, false);
            } else {
              setCurrentTime(lesson.progressInSeconds, false);
            }
          }, 500);
        }
      }
    }
  }, [lessonId]);

  const viewLessonHandler = () => {
    api
      .callApi("isLessonViewed", {
        params: { pk: store.currentLesson.id },
      })
      .then((res) => {
        console.log(`Lesson ${store.currentLesson.id} viewed!`);
        if (store.currentLesson?.progress < 0) {
          store.currentLesson.updateProgress(0);
        }
      });
  };

  const nextLessonHandler = () => {
    let lessons = [];
    store.chapters.forEach((chapter) => {
      const lesson_id = chapter.lessons.reduce((a, b) => {
        return _.concat(a, b.id);
      }, []);
      lessons = _.concat(lessons, lesson_id);
    });

    const next_lesson_id = lessons[lessons.indexOf(store.currentLesson.id) + 1];
    if (next_lesson_id) {
      navigate(`/lesson/${store.course.id}/${next_lesson_id}`);

      // Autoplay next lesson
      setTimeout(() => {
        playerRef.current.play();
      }, 1000);
    }
  };

  useEffect(() => {
    // Every 5s update progress
    setInterval(() => {
      const player = playerRef.current;
      const isVideoPlaying =
        player?.currentTime() && !player.paused() && !player.ended() && player.readyState() > 2;
      if (isVideoPlaying && store.currentLesson) {
        api
          .callApi("updateProgress", {
            params: { pk: store.currentLesson.id },
            body: { progress: store.currentLessonProgress },
          })
          .then((res) => {
            // console.log("Progress updated: ", store.currentLessonProgress);
          });
      }
    }, 5000);
  }, []);

  const setCurrentTime = (time, autoplay = true) => {
    const player = playerRef.current;

    player.play();
    player.currentTime(time);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <Row className="g-4 mt-0 mb-5 pb-4">
        <Col lg={{ span: 8 }}>
          {store.currentLesson?.lesson_type === "video" &&
            store.currentLesson?.video &&
            store.currentLesson.video.qualities?.length > 0 && (
              <VideoJS
                ref={playerRef}
                options={{
                  ...VIDEOJS_OPTIONS,
                  poster: store.currentLesson.video.thumbnail_url,
                  sources: store.currentLesson.video.qualities.map((quality) => ({
                    type: quality.name === "hls" ? "application/x-mpegURL" : quality.type,
                    name: quality.width,
                    src: quality.url,
                  })),
                }}
                onReady={(player) => {
                  player.on("timeupdate", () => store.setCurrentTime(player.currentTime()));
                  player.on("ended", nextLessonHandler);
                }}
              />
            )}
          {!store.currentLesson && (
            <div className="no-lesson-found mb-4 rounded-10 text-black-50">
              {isMobileOrTablet ? (
                <i className="fa-thin fa-hand-back-point-down fa-3x mb-3" />
              ) : (
                <i className="fa-thin fa-hand-point-right fa-3x mb-3" />
              )}
              Hãy chọn bài để bắt đầu học!
            </div>
          )}
          <Tabs setCurrentTime={setCurrentTime} />
        </Col>

        <MediaQuery minWidth={992}>
          <Col lg={{ span: 4 }}>
            <Playlist />
          </Col>
        </MediaQuery>
      </Row>

      <RatingModal />
      <ReportModal />
      <LessonModal />
      <ChapterModal />
      <CommentModal />
      <ReactionModal />
      <ProductDetailModal />
      <AddToCartModal />
      <QuestionModal />

      <ToastContainer
        theme="colored"
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        draggable
        pauseOnHover
        closeOnClick
      />
    </>
  );
});

export default App;
